import React from "react";
import { FiSettings } from "react-icons/fi";
import { useLocation } from "@reach/router";
import { BaseMemberPaths } from "newui/utils/navigation/navigation.types";
import { useCurrentUserHasFeatureFlag, useHasNotificationsConfiguration, useHasRfxSettings } from "hooks/featureFlags";
import { CurrentAccountType, useProfileContext } from "newui/features/Auth/context/ProfileContext";
import { AppMenuItem } from "./AppMenuItem/AppMenuItem";
import { ExpandableMenuItem, ExpandableMenuChildItem } from "./ExpandableMenuItem";
import { useIsCurrentUserAdmin } from "hooks/auth";
import { FeatureFlagType } from "newui/features/Auth/types/FeatureFlag.type";

interface SettingsExpandableMenuProps {
	isCollapsed: boolean;
	paths: BaseMemberPaths;
	onNavigate?: () => void;
}

const STORAGE_KEY = "settingsMenu_expanded";

export const SettingsExpandableMenu: React.FC<SettingsExpandableMenuProps> = ({
	isCollapsed,
	paths,
	onNavigate,
}) => {
	const isAdminUser = useIsCurrentUserAdmin();
	const hasRfxSettings = useHasRfxSettings();
	const profileContext = useProfileContext();
	const isSupplierReportBrandingEnabled = useCurrentUserHasFeatureFlag(FeatureFlagType.ENABLE_REPORT_BRANDING_FOR_SUPPLIER);
	const hasNotificationsConfiguration = useHasNotificationsConfiguration();
	const isProcurer = profileContext?.currentProfile === CurrentAccountType.PROCURER;
	const location = useLocation();
	const currentPath = location.pathname;

	const accountSettingsLink = paths.accountSettings.userManagement;
	const usersLink = paths.accountSettings.users.list;
	const rfxSettingsLink = paths.rfxSettings;
	const pdfSettingsLink = paths.pdfSettings;
	const notificationSettingsLink = paths.notificationSettings;
	const rolesLink = paths.accountSettings.roles;
	const auditLink = paths.accountSettings.audit;

	const isAccountSettingsActive = currentPath.includes("account-settings") && !currentPath.includes("account-settings/users") && !currentPath.includes("account-settings/roles") && !currentPath.includes("account-settings/audit");
	const isUsersActive = currentPath.includes("account-settings/users");
	const isRfxSettingsActive = currentPath.includes("rfx-settings");
	const isPdfSettingsActive = currentPath.includes("pdf-settings");
	const isNotificationSettingsActive = currentPath.includes("settings/notifications");
	const isRolesActive = currentPath.includes("account-settings/roles");
	const isAuditActive = currentPath.includes("account-settings/audit");

	const showRfxSettings = (hasRfxSettings && isProcurer) || !isProcurer;
	const hasChildren = showRfxSettings || isProcurer || isAdminUser;

	if (!isAdminUser) {
		return null;
	}

	if (!hasChildren) {
		return (
			<AppMenuItem
				name="Settings"
				icon={<FiSettings size={24} />}
				link={accountSettingsLink}
				isCollapsed={isCollapsed}
				testId="account-settings"
				tourName="AccountSettings"
				onNavigate={onNavigate}
			/>
		);
	}

	return (
		<ExpandableMenuItem
			icon={<FiSettings size={24} />}
			label="Settings"
			storageKey={STORAGE_KEY}
			isCollapsed={isCollapsed}
			collapsedLink={accountSettingsLink}
			testId="settings-menu"
			onNavigate={onNavigate}
		>
			<ExpandableMenuChildItem
				name="Account Settings"
				link={accountSettingsLink}
				isActive={isAccountSettingsActive}
				testId="account-settings-child"
				tourName="AccountSettings"
				onNavigate={onNavigate}
			/>
			<ExpandableMenuChildItem
				name="User Management"
				link={usersLink}
				isActive={isUsersActive}
				testId="users-child"
				onNavigate={onNavigate}
				tourName="UserManagement"
			/>
			<ExpandableMenuChildItem
				name="Roles & Permissions"
				link={rolesLink}
				isActive={isRolesActive}
				testId="roles-permissions-child"
				onNavigate={onNavigate}
			/>
			<ExpandableMenuChildItem
				name="Audit Trail"
				link={auditLink}
				isActive={isAuditActive}
				testId="audit-trail-child"
				onNavigate={onNavigate}
			/>
			{showRfxSettings && (
				<ExpandableMenuChildItem
					name="RFx Settings"
					link={rfxSettingsLink}
					isActive={isRfxSettingsActive}
					testId="rfx-settings-child"
					onNavigate={onNavigate}
				/>
			)}
			{(isSupplierReportBrandingEnabled) && (
				<ExpandableMenuChildItem
					name="PDF Report Settings"
					link={pdfSettingsLink}
					isActive={isPdfSettingsActive}
					testId="pdf-settings-child"
					onNavigate={onNavigate}
				/>
			)}
			{hasNotificationsConfiguration && (
				<ExpandableMenuChildItem
					name="Notification Settings"
					link={notificationSettingsLink}
					isActive={isNotificationSettingsActive}
					testId="notification-settings-child"
					onNavigate={onNavigate}
				/>
			)}
		</ExpandableMenuItem>
	);
};
